import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, FileText, Printer, Trash2, Mail, BadgeCheck } from "lucide-react";

const fmtD = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
};

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

export const printInvoice = (inv) => {
  const iss = inv.issuer || {};
  const rec = inv.recipient || {};
  const html = `<!DOCTYPE html><html lang="sl"><head><meta charset="utf-8"><title>Račun ${inv.invoice_number}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1e293b;margin:0;font-size:13px;background:#fff}
  .sheet{max-width:720px;margin:0 auto;padding:32px}
  .banner{background:#1e3a1e;border-radius:14px;padding:24px 30px;display:flex;justify-content:space-between;align-items:center;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .brand{display:flex;align-items:center;gap:14px}
  .brand img{width:52px;height:52px;border-radius:12px;display:block}
  .brand .nm{color:#fff;font-size:19px;font-weight:700;letter-spacing:.3px}
  .brand .dm{color:#a3c9a3;font-size:12px}
  .invno{text-align:right}
  .invno .lbl{color:#a3c9a3;font-size:11px;text-transform:uppercase;letter-spacing:1.5px}
  .invno .no{color:#fff;font-size:22px;font-weight:700}
  .parties{display:flex;justify-content:space-between;margin:28px 0 8px;gap:24px}
  .box{line-height:1.7;color:#334155}
  .box .cap{font-size:10px;text-transform:uppercase;letter-spacing:1.2px;color:#94a3b8;margin-bottom:6px}
  .box strong{font-size:14px}
  .dates{background:#f6f8f6;border-radius:10px;padding:12px 18px;display:flex;justify-content:space-between;font-size:12px;color:#475569;margin-top:14px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  table{width:100%;border-collapse:collapse;margin-top:22px}
  th{background:#1e3a1e;color:#fff;padding:10px 12px;text-align:left;font-weight:600;font-size:12px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  th:first-child{border-radius:8px 0 0 0}th:last-child{border-radius:0 8px 0 0;text-align:right}
  td{padding:14px 12px;border-bottom:1px solid #e2e8f0;color:#334155}
  td .sub{color:#94a3b8;font-size:12px}
  .totwrap{display:flex;justify-content:flex-end;margin-top:18px}
  .tot{background:#1e3a1e;border-radius:10px;padding:14px 24px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .tot .l{color:#a3c9a3;font-size:12px;letter-spacing:.5px}
  .tot .v{color:#fff;font-size:20px;font-weight:700;margin-left:10px}
  .note{margin-top:20px;color:#475569;line-height:1.6}
  .foot{margin-top:28px;border-top:1px solid #e2e8f0;padding-top:16px;font-size:11px;color:#94a3b8;line-height:1.7}
  .foot strong{color:#475569}
  .foot a{color:#1e3a1e;text-decoration:none}
  .stamp{position:absolute;top:190px;left:50%;transform:translateX(-50%) rotate(-8deg);border:4px solid #16a34a;color:#16a34a;font-weight:800;font-size:30px;letter-spacing:5px;padding:10px 30px;border-radius:12px;text-align:center;opacity:.85;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .stamp .d{font-size:12px;letter-spacing:1px;font-weight:600;margin-top:2px}
  .sheet{position:relative}
</style></head><body>
<div class="sheet">
${inv.placano ? `<div class="stamp">PLAČANO</div>` : ""}
<div class="banner">
  <div class="brand">
    <img src="https://doberpogled.app/dp-192.png" alt="Dober pogled">
    <div><div class="nm">Dober pogled</div><div class="dm">doberpogled.app</div></div>
  </div>
  <div class="invno"><div class="lbl">Račun</div><div class="no">št. ${inv.invoice_number}</div></div>
</div>
<div class="parties">
  <div class="box"><div class="cap">Izdajatelj</div><strong>${iss.naziv || ""}</strong><br>${iss.naslov || ""}<br>${iss.posta || ""}
    ${iss.davcna ? `<br>Davčna št.: ${iss.davcna}` : ""}${iss.iban ? `<br>IBAN: ${iss.iban}` : ""}${iss.email ? `<br>${iss.email}` : ""}</div>
  <div class="box" style="text-align:right"><div class="cap">Prejemnik</div><strong>${rec.naziv || inv.ld_naziv || ""}</strong><br>${rec.naslov || ""}<br>${rec.posta || ""}${rec.davcna ? `<br>Davčna št.: ${rec.davcna}` : ""}</div>
</div>
<div class="dates"><span>Datum izdaje: <strong>${fmtD(inv.datum_izdaje)}</strong></span><span>Rok plačila: <strong>${fmtD(inv.rok_placila)}</strong></span></div>
<table><thead><tr><th>Opis storitve</th><th>Obdobje</th><th>Znesek</th></tr></thead>
<tbody><tr>
  <td>${inv.plan_label} – aplikacija Dober pogled<br><span class="sub">${inv.ld_naziv}</span></td>
  <td style="white-space:nowrap">${inv.obdobje_od ? `${fmtD(inv.obdobje_od)} – ${fmtD(inv.obdobje_do)}` : "—"}</td>
  <td style="text-align:right;white-space:nowrap">${eur(inv.amount)}</td>
</tr></tbody></table>
<div class="totwrap"><div class="tot"><span class="l">SKUPAJ ZA PLAČILO</span><span class="v">${eur(inv.amount)}</span></div></div>
${inv.opomba ? `<p class="note">${inv.opomba}</p>` : ""}
<div class="foot">Pri plačilu navedite sklic: <strong>${inv.invoice_number}</strong><br>Hvala za zaupanje! · Dober pogled app · <a href="https://doberpogled.app">doberpogled.app</a></div>
</div>
<script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Brskalnik je blokiral pojavno okno"); return; }
  w.document.write(html);
  w.document.close();
};

export const SAInvoiceDialog = ({ ld, token, onClose }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [existing, setExisting] = useState([]);
  const [f, setF] = useState(null);

  useEffect(() => {
    if (!ld) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/superadmin/invoices/prefill/${ld.id}`, { headers }),
      axios.get(`${API}/superadmin/invoices?ld_id=${ld.id}`, { headers }),
    ]).then(([p, l]) => {
      const d = p.data;
      setPlans(d.plans || []);
      setExisting(l.data.invoices || []);
      setF({
        invoice_number: "",
        plan: d.plan,
        amount: d.amount,
        datum_izdaje: d.datum_izdaje,
        rok_placila: d.rok_placila,
        obdobje_od: d.obdobje_od,
        obdobje_do: d.obdobje_do,
        opomba: "",
        issuer: d.issuer,
        recipient: d.recipient,
        last_invoice_number: d.last_invoice_number,
        podaljsaj_narocnino: true,
      });
    }).catch((e) => {
      toast.error(e.response?.data?.detail || "Napaka pri nalaganju");
      onClose();
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ld]);

  const setPlan = (key) => {
    const p = plans.find((x) => x.key === key);
    setF((prev) => ({ ...prev, plan: key, amount: p ? p.amount : prev.amount }));
  };

  const save = async () => {
    if (!f.invoice_number.trim()) { toast.error("Vnesite številko računa"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/superadmin/invoices`, {
        ld_id: ld.id,
        invoice_number: f.invoice_number.trim(),
        plan: f.plan,
        amount: parseFloat(String(f.amount).replace(",", ".")),
        datum_izdaje: f.datum_izdaje,
        rok_placila: f.rok_placila,
        obdobje_od: f.obdobje_od,
        obdobje_do: f.obdobje_do,
        opomba: f.opomba,
        issuer: f.issuer,
        recipient: f.recipient,
        podaljsaj_narocnino: !!f.podaljsaj_narocnino,
      }, { headers });
      toast.success(`Račun ${res.data.invoice_number} izdan`);
      setExisting((prev) => [res.data, ...prev]);
      setF((prev) => ({ ...prev, invoice_number: "", last_invoice_number: res.data.invoice_number }));
      printInvoice(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri izdaji računa");
    } finally {
      setSaving(false);
    }
  };

  const del = async (inv) => {
    if (!window.confirm(`Izbrišem račun ${inv.invoice_number}?`)) return;
    try {
      await axios.delete(`${API}/superadmin/invoices/${inv.id}`, { headers });
      setExisting((prev) => prev.filter((x) => x.id !== inv.id));
      toast.success("Račun izbrisan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const sendMail = async (inv) => {
    const email = window.prompt("Pošlji račun na e-poštni naslov:", inv.sent_to || ld.kontakt_email || "");
    if (email === null) return;
    try {
      const res = await axios.post(`${API}/superadmin/invoices/${inv.id}/send`, { email: email.trim() }, { headers });
      toast.success(res.data.message);
      setExisting((prev) => prev.map((x) => (x.id === inv.id ? { ...x, sent_to: res.data.sent_to, sent_at: new Date().toISOString() } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri pošiljanju");
    }
  };

  const markPaid = async (inv) => {
    const extend = inv.podaljsaj_narocnino !== false;
    const msg = extend
      ? `Označim račun ${inv.invoice_number} kot PLAČAN in podaljšam naročnino LD (${inv.plan_label})?`
      : `Označim račun ${inv.invoice_number} kot PLAČAN (brez podaljšanja naročnine)?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${API}/superadmin/invoices/${inv.id}/mark-paid`, {}, { headers });
      toast.success(res.data.subscription_ends_at ? `${res.data.message} — do ${fmtD(res.data.subscription_ends_at)}` : res.data.message);
      setExisting((prev) => prev.map((x) => (x.id === inv.id ? { ...x, placano: true, placano_at: new Date().toISOString(), subscription_extended_to: res.data.subscription_ends_at } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const upd = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const updIss = (k, v) => setF((prev) => ({ ...prev, issuer: { ...prev.issuer, [k]: v } }));
  const updRec = (k, v) => setF((prev) => ({ ...prev, recipient: { ...prev.recipient, [k]: v } }));
  const selectCls = "w-full h-9 rounded-md border border-input bg-background px-2 text-sm";

  return (
    <Dialog open={!!ld} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sa-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Izdaj račun – {ld?.naziv}</DialogTitle>
          <DialogDescription>Podatki so predizpolnjeni po izbrani naročnini. Vnesite le številko računa.</DialogDescription>
        </DialogHeader>
        {loading || !f ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Številka računa *{f.last_invoice_number ? ` (zadnja: ${f.last_invoice_number})` : ""}</Label>
                <Input value={f.invoice_number} onChange={(e) => upd("invoice_number", e.target.value)} placeholder="npr. 2026-001" data-testid="invoice-number-input" />
              </div>
              <div>
                <Label className="text-xs">Naročnina</Label>
                <select value={f.plan} onChange={(e) => setPlan(e.target.value)} className={selectCls} data-testid="invoice-plan-select">
                  {plans.map((p) => <option key={p.key} value={p.key}>{p.label} – {eur(p.amount)}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Znesek (€)</Label>
                <Input type="number" step="0.01" value={f.amount} onChange={(e) => upd("amount", e.target.value)} data-testid="invoice-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Datum izdaje</Label>
                <Input type="date" value={f.datum_izdaje} onChange={(e) => upd("datum_izdaje", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Rok plačila</Label>
                <Input type="date" value={f.rok_placila} onChange={(e) => upd("rok_placila", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Obdobje od</Label>
                  <Input type="date" value={f.obdobje_od || ""} onChange={(e) => upd("obdobje_od", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Obdobje do</Label>
                  <Input type="date" value={f.obdobje_do || ""} onChange={(e) => upd("obdobje_do", e.target.value)} />
                </div>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer select-none border rounded-lg p-3 bg-muted/40" data-testid="invoice-extend-checkbox-label">
              <input
                type="checkbox"
                checked={!!f.podaljsaj_narocnino}
                onChange={(e) => upd("podaljsaj_narocnino", e.target.checked)}
                className="h-4 w-4 accent-green-800"
                data-testid="invoice-extend-checkbox"
              />
              <span>Ob plačilu <strong>podaljšaj obdobje naročnine</strong> LD (če ni obkljukano, se samo izda račun)</span>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-semibold">Izdajatelj (zapomnim si)</p>
                <Input className="h-8 text-xs" placeholder="Naziv" value={f.issuer.naziv || ""} onChange={(e) => updIss("naziv", e.target.value)} data-testid="issuer-naziv" />
                <Input className="h-8 text-xs" placeholder="Naslov" value={f.issuer.naslov || ""} onChange={(e) => updIss("naslov", e.target.value)} />
                <Input className="h-8 text-xs" placeholder="Pošta in kraj" value={f.issuer.posta || ""} onChange={(e) => updIss("posta", e.target.value)} />
                <Input className="h-8 text-xs" placeholder="Davčna številka" value={f.issuer.davcna || ""} onChange={(e) => updIss("davcna", e.target.value)} />
                <Input className="h-8 text-xs" placeholder="IBAN" value={f.issuer.iban || ""} onChange={(e) => updIss("iban", e.target.value)} />
                <Input className="h-8 text-xs" placeholder="E-pošta" value={f.issuer.email || ""} onChange={(e) => updIss("email", e.target.value)} />
              </div>
              <div className="border rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-semibold">Prejemnik (zapomnim si na LD)</p>
                <Input className="h-8 text-xs" placeholder="Naziv" value={f.recipient.naziv || ""} onChange={(e) => updRec("naziv", e.target.value)} data-testid="recipient-naziv" />
                <Input className="h-8 text-xs" placeholder="Naslov" value={f.recipient.naslov || ""} onChange={(e) => updRec("naslov", e.target.value)} />
                <Input className="h-8 text-xs" placeholder="Pošta in kraj" value={f.recipient.posta || ""} onChange={(e) => updRec("posta", e.target.value)} />
                <Input className="h-8 text-xs" placeholder="Davčna številka" value={f.recipient.davcna || ""} onChange={(e) => updRec("davcna", e.target.value)} />
              </div>
            </div>

            <div>
              <Label className="text-xs">Opomba na računu (neobvezno)</Label>
              <Input value={f.opomba} onChange={(e) => upd("opomba", e.target.value)} placeholder="npr. Hvala za zaupanje." />
            </div>

            {existing.length > 0 && (
              <div className="border rounded-lg p-2">
                <p className="text-xs font-semibold mb-1">Izdani računi za to LD</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {existing.map((inv) => (
                    <div key={inv.id} className="flex items-center gap-2 text-xs bg-muted/40 rounded px-2 py-1" data-testid={`invoice-row-${inv.invoice_number}`}>
                      <span className="font-medium">{inv.invoice_number}</span>
                      <span className="text-muted-foreground">{fmtD(inv.datum_izdaje)} · {inv.plan_label} · {eur(inv.amount)}</span>
                      {inv.placano && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5" data-testid={`invoice-paid-${inv.invoice_number}`}>PLAČAN</span>}
                      {inv.sent_to && !inv.placano && <span className="text-[10px] text-sky-700 bg-sky-100 rounded px-1.5 py-0.5" title={`Poslano na ${inv.sent_to}`}>POSLAN</span>}
                      <span className="ml-auto flex gap-1">
                        <button className="p-1 hover:text-primary" title={`Pošlji po e-pošti${inv.sent_to ? ` (poslano: ${inv.sent_to})` : ""}`} onClick={() => sendMail(inv)} data-testid={`invoice-send-${inv.invoice_number}`}><Mail className="h-3.5 w-3.5" /></button>
                        {!inv.placano && (
                          <button className="p-1 hover:text-emerald-700" title="Označi kot plačan + podaljšaj naročnino" onClick={() => markPaid(inv)} data-testid={`invoice-paid-btn-${inv.invoice_number}`}><BadgeCheck className="h-3.5 w-3.5" /></button>
                        )}
                        <button className="p-1 hover:text-primary" title="Natisni" onClick={() => printInvoice(inv)} data-testid={`invoice-print-${inv.invoice_number}`}><Printer className="h-3.5 w-3.5" /></button>
                        <button className="p-1 hover:text-red-600" title="Izbriši" onClick={() => del(inv)}><Trash2 className="h-3.5 w-3.5" /></button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Zapri</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="invoice-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-1.5" />Izdaj račun in natisni</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
